import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { PlannerMapDataElement, VisibleAsset } from 'components/common/types/Planner.types';
import {
  getAllocatedFrames,
  getAllocatedImpressions,
  getAssetsDictionary,
} from 'store/dealManagement/selectors/availabilityAssets';
import { getIsSharedCampaign } from 'store/sharedCampaignPlan/selectors';

interface UseMapAssetsTransformOptions {
  isAllocated?: boolean;
}

export const useMapAssetsTransform = (
  options: UseMapAssetsTransformOptions = {},
): { mapAssetsToMapElements: (assets: VisibleAsset[]) => PlannerMapDataElement[] } => {
  const assetsDictionary = useSelector(getAssetsDictionary);
  const allocatedFrames = useSelector(getAllocatedFrames);
  const allocatedImpressions = useSelector(getAllocatedImpressions);
  const isSharedCampaign = useSelector(getIsSharedCampaign);

  const defaultIsAllocated = !!(allocatedFrames && allocatedImpressions);
  const isAllocated = options.isAllocated ?? defaultIsAllocated;

  const mapAssetsToMapElements = useCallback(
    (visibleAssets: VisibleAsset[]): PlannerMapDataElement[] => {
      return visibleAssets.map<PlannerMapDataElement>((visibleAsset) => {
        const isVisible = isSharedCampaign || assetsDictionary.has(visibleAsset.frameId);

        return {
          isVisible,
          index: visibleAsset.index,
          name: visibleAsset.frameId,
          geometry: {
            coordinates: [visibleAsset.longitude, visibleAsset.latitude],
          },
          properties: {
            ...visibleAsset,
            isVisible,
            allocatedImpressions: isAllocated ? (visibleAsset.allocatedImpressions ?? visibleAsset.impressions) : 0,
          },
        };
      });
    },
    [assetsDictionary, isAllocated, isSharedCampaign],
  );

  return { mapAssetsToMapElements };
};
